import React, { useState } from 'react';
import { Part } from '../types';
import './FormulaireRepartition.css';

type Preset = 'lineaire' | 'inverse' | 'egal' | 'aleatoire' | 'custom';

// Plage bloquée entre 08h00 et 17h30
const HEURE_MIN = 8;
const HEURE_MAX = 17;
const MINUTE_MAX_FIN = 30; // à 17h, la fin max est :30
const START_MINS = HEURE_MIN * 60;       // 480
const END_MINS   = HEURE_MAX * 60 + MINUTE_MAX_FIN; // 1050
const HEURES_PLAGE = Array.from({ length: HEURE_MAX - HEURE_MIN + 1 }, (_, i) => i + HEURE_MIN); // 8..17
const MINUTES_OPTIONS = [0, 15, 30, 45];

function fmtH(h: number): string {
  return `${String(h).padStart(2, '0')}h`;
}
function fmtM(m: number): string {
  return String(m).padStart(2, '0');
}
function fmtTime(h: number, m: number): string {
  return `${fmtH(h)}${fmtM(m)}`;
}

interface Plage {
  heureDebut: number;
  minuteDebut: number;
  heureFin: number;
  minuteFin: number;
}

function snapTo15(mins: number): number {
  return Math.round(mins / 15) * 15;
}

function clampMins(mins: number): number {
  return Math.max(START_MINS, Math.min(END_MINS, mins));
}

function minsToPlage(debut: number, fin: number): Plage {
  debut = clampMins(debut);
  fin   = clampMins(fin);
  return {
    heureDebut:  Math.floor(debut / 60),
    minuteDebut: debut % 60,
    heureFin:    Math.floor(fin / 60),
    minuteFin:   fin % 60,
  };
}

function genererPlages(n: number): Plage[] {
  const total = END_MINS - START_MINS; // 570 min
  return Array.from({ length: n }, (_, i) => {
    const debutMins = i === 0
      ? START_MINS
      : clampMins(snapTo15(START_MINS + i * total / n));
    const finMins = i === n - 1
      ? END_MINS
      : clampMins(snapTo15(START_MINS + (i + 1) * total / n) - 15);
    return minsToPlage(debutMins, finMins);
  });
}

function poidsAleatoires(n: number): number[] {
  return Array.from({ length: n }, () => Math.floor(Math.random() * 20) + 1);
}

function plagesAleatoires(n: number): Plage[] {
  // Points valides : snap 15 min dans [START_MINS, END_MINS]
  const slots: number[] = [];
  for (let m = START_MINS; m <= END_MINS; m += 15) slots.push(m);
  return Array.from({ length: n }, () => {
    const startIdx = Math.floor(Math.random() * (slots.length - 1));
    const endIdx   = startIdx + 1 + Math.floor(Math.random() * (slots.length - startIdx - 1));
    return minsToPlage(slots[startIdx], slots[Math.min(endIdx, slots.length - 1)]);
  });
}

const POIDS_PRESETS: Record<Exclude<Preset, 'custom' | 'aleatoire'>, (n: number) => number[]> = {
  lineaire: (n) => Array.from({ length: n }, (_, i) => i + 1),
  egal:     (n) => Array.from({ length: n }, () => 1),
  inverse:  (n) => Array.from({ length: n }, (_, i) => n - i),
};

interface Props {
  onCalculer: (montant: number, parts: Part[], base: number, zone: string, limitePart: number, controleur: string) => string | null;
}

export default function FormulaireRepartition({ onCalculer }: Props) {
  const [montant, setMontant]         = useState('');
  const [zone, setZone]               = useState('');
  const [controleur, setControleur]   = useState('');
  const [nbParts, setNbParts]         = useState('');
  const [base, setBase]               = useState(50);
  const [limitePart, setLimitePart]   = useState(5_000_000);
  const [poids, setPoids]           = useState<number[]>([]);
  const [noms, setNoms]             = useState<string[]>([]);
  const [plages, setPlages]         = useState<Plage[]>([]);
  const [preset, setPreset]         = useState<Preset>('lineaire');
  const [etape, setEtape]           = useState(1);
  const [errors, setErrors]         = useState<Record<string, string>>({});
  const [erreurCalcul, setErreurCalcul] = useState<string | null>(null);

  function initParts(n: number, p: Preset): void {
    if (p === 'aleatoire') {
      setPoids(poidsAleatoires(n));
      setPlages(plagesAleatoires(n));
    } else {
      if (p !== 'custom') setPoids(POIDS_PRESETS[p](n));
      else setPoids(Array(n).fill(1));
      setPlages(genererPlages(n));
    }
    setNoms(Array.from({ length: n }, (_, i) => `Personne ${i + 1}`));
  }

  function handleNbParts(val: string): void {
    const n = parseInt(val) || 0;
    setNbParts(val);
    if (n >= 2 && n <= 30) initParts(n, preset);
  }

  function handlePreset(p: Preset): void {
    setPreset(p);
    const n = parseInt(nbParts) || 0;
    if (n >= 2) {
      if (p === 'aleatoire') {
        setPoids(poidsAleatoires(n));
        setPlages(plagesAleatoires(n));
      } else if (p !== 'custom') {
        setPoids(POIDS_PRESETS[p](n));
        setPlages(genererPlages(n));
      }
    }
  }

  function retirerAleatoire(): void {
    const n = parseInt(nbParts) || 0;
    if (n >= 2) {
      setPoids(poidsAleatoires(n));
      setPlages(plagesAleatoires(n));
    }
  }

  function handlePoids(i: number, val: string): void {
    const newP = [...poids];
    newP[i] = Math.max(1, parseInt(val) || 1);
    setPoids(newP);
  }

  function handleNom(i: number, val: string): void {
    const newN = [...noms];
    newN[i] = val;
    setNoms(newN);
  }

  function handlePlage(i: number, field: keyof Plage, val: number): void {
    const newP = [...plages];
    const updated = { ...newP[i], [field]: val };
    // Bloquer la fin à 17h30 max
    if (updated.heureFin === HEURE_MAX && updated.minuteFin > MINUTE_MAX_FIN) {
      updated.minuteFin = MINUTE_MAX_FIN;
    }
    // Bloquer le début à 08h00 min
    if (updated.heureDebut === HEURE_MIN && updated.minuteDebut < 0) {
      updated.minuteDebut = 0;
    }
    newP[i] = updated;
    setPlages(newP);
  }

  // Options minutes pour la fin : si heure=17, seulement :00 et :30
  function minutesFinOptions(i: number): number[] {
    return plages[i]?.heureFin === HEURE_MAX
      ? MINUTES_OPTIONS.filter(m => m <= MINUTE_MAX_FIN)
      : MINUTES_OPTIONS;
  }

  function validerEtape1(): void {
    const errs: Record<string, string> = {};
    const m = parseFloat(montant.replace(/\s/g, '').replace(',', '.'));
    if (!montant || isNaN(m) || m <= 0) errs.montant = 'Entrez un montant valide';
    const n = parseInt(nbParts);
    if (!nbParts || isNaN(n) || n < 2 || n > 30) errs.nbParts = 'Entre 2 et 30 parts';
    setErrors(errs);
    if (Object.keys(errs).length === 0) {
      handleNbParts(nbParts);
      setEtape(2);
    }
  }

  function handleSubmit(): void {
    const m = parseFloat(montant.replace(/\s/g, '').replace(',', '.'));
    const n = parseInt(nbParts);
    const partsList: Part[] = Array.from({ length: n }, (_, i) => ({
      nom:         noms[i] || `Personne ${i + 1}`,
      poids:       poids[i] || 1,
      heureDebut:  plages[i]?.heureDebut  ?? HEURE_MIN,
      minuteDebut: plages[i]?.minuteDebut ?? 0,
      heureFin:    plages[i]?.heureFin    ?? HEURE_MAX,
      minuteFin:   plages[i]?.minuteFin   ?? MINUTE_MAX_FIN,
    }));
    const erreur = onCalculer(m, partsList, base, zone, limitePart, controleur);
    if (erreur) { setErreurCalcul(erreur); return; }
    setErreurCalcul(null);
    setEtape(1);
    setMontant('');
    setZone('');
    setNbParts('');
    setPoids([]);
    setNoms([]);
    setPlages([]);
  }

  function retour(): void { setEtape(1); setErrors({}); setErreurCalcul(null); }

  const totalPoids = poids.reduce((s, p) => s + (p || 0), 0);

  return (
    <div className="form-card">
      <div className="stepper">
        <div className={`step ${etape >= 1 ? 'active' : ''}`}>
          <div className="step-num">1</div>
          <span>Montant &amp; parts</span>
        </div>
        <div className="step-line" />
        <div className={`step ${etape >= 2 ? 'active' : ''}`}>
          <div className="step-num">2</div>
          <span>Poids, horaires &amp; noms</span>
        </div>
      </div>

      {etape === 1 && (
        <div className="form-section">
          <div className="form-group">
            <label className="form-label" htmlFor="montant">
              Montant total à répartir <span className="form-unit">FCFA</span>
            </label>
            <input
              id="montant"
              type="number"
              placeholder="ex : 20 000 000"
              value={montant}
              onChange={e => { setMontant(e.target.value); setErrors(er => ({ ...er, montant: '' })); }}
              min="1"
            />
            {errors.montant && <p className="form-error">{errors.montant}</p>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="zone">Zone / PDV</label>
            <input
              id="zone"
              type="text"
              placeholder="ex : Marché Central, Zone A…"
              value={zone}
              onChange={e => setZone(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="controleur">Nom du contrôleur</label>
            <input
              id="controleur"
              type="text"
              placeholder="ex : Jean Dupont"
              value={controleur}
              onChange={e => setControleur(e.target.value)}
            />
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="nbParts">Nombre de parts</label>
            <input
              id="nbParts"
              type="number"
              placeholder="ex : 10"
              value={nbParts}
              onChange={e => { setNbParts(e.target.value); setErrors(er => ({ ...er, nbParts: '' })); }}
              min="2" max="30"
            />
            {errors.nbParts && <p className="form-error">{errors.nbParts}</p>}
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="base">Arrondi au multiple de <span className="form-unit">FCFA</span></label>
            <input
              id="base"
              type="number"
              value={base}
              onChange={e => setBase(Math.max(1, parseInt(e.target.value) || 50))}
              min="1"
            />
            <div className="base-btns">
              {([10, 50, 100, 500, 1000, 5000, 10000] as const).map(b => (
                <button key={b} type="button" className={`base-btn ${base === b ? 'selected' : ''}`} onClick={() => setBase(b)}>
                  {b}
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label className="form-label" htmlFor="limitePart">
              Limite par bénéficiaire <span className="form-unit">FCFA</span>
            </label>
            <input
              id="limitePart"
              type="number"
              value={limitePart}
              onChange={e => setLimitePart(Math.max(1, parseInt(e.target.value) || 5_000_000))}
              min="1"
            />
            <div className="base-btns">
              {([1_000_000, 2_000_000, 5_000_000, 10_000_000] as const).map(v => (
                <button key={v} type="button"
                  className={`base-btn ${limitePart === v ? 'selected' : ''}`}
                  onClick={() => setLimitePart(v)}
                >
                  {(v / 1_000_000).toLocaleString('fr-FR')} M
                </button>
              ))}
            </div>
          </div>

          <button className="btn-primary" onClick={validerEtape1}>
            Suivant — Configurer les poids
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="9 18 15 12 9 6"/>
            </svg>
          </button>
        </div>
      )}

      {etape === 2 && (
        <div className="form-section">
          <div className="recap-band">
            {zone && <><span><strong>{zone}</strong></span><span className="recap-sep">·</span></>}
            <span><strong>{montant}</strong> FCFA</span>
            <span className="recap-sep">·</span>
            <span><strong>{nbParts}</strong> parts</span>
            <span className="recap-sep">·</span>
            <span>Multiple de <strong>{base}</strong></span>
          </div>

          <div className="form-group">
            <label className="form-label">Distribution des poids</label>
            <div className="preset-btns">
              {([
                { key: 'lineaire'  as Preset, label: 'Croissant (1→n)' },
                { key: 'inverse'   as Preset, label: 'Décroissant (n→1)' },
                { key: 'egal'      as Preset, label: 'Égal (1,1…)' },
                { key: 'aleatoire' as Preset, label: 'Aléatoire' },
                { key: 'custom'    as Preset, label: 'Manuel' },
              ]).map(p => (
                <button key={p.key} type="button" className={`preset-btn ${preset === p.key ? 'selected' : ''}`} onClick={() => handlePreset(p.key)}>
                  {p.key === 'aleatoire' && (
                    <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="16 3 21 3 21 8"/><line x1="4" y1="20" x2="21" y2="3"/>
                      <polyline points="21 16 21 21 16 21"/><line x1="15" y1="15" x2="21" y2="21"/>
                    </svg>
                  )}
                  {p.label}
                </button>
              ))}
            </div>
            {preset === 'aleatoire' && (
              <button type="button" className="btn-retirer" onClick={retirerAleatoire}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="23 4 23 10 17 10"/><polyline points="1 20 1 14 7 14"/>
                  <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
                </svg>
                Nouveaux poids &amp; horaires aléatoires
              </button>
            )}
          </div>

          <div className="parts-grid">
            <div className="parts-grid-header parts-grid-header--5">
              <span>#</span>
              <span>Nom / libellé</span>
              <span>Plage horaire <span className="header-hint">08h00 – 17h30</span></span>
              <span>Poids</span>
              <span>%</span>
            </div>
            <div className="parts-rows">
              {poids.map((p, i) => (
                <div key={i} className="parts-row parts-row--5">
                  <span className="row-num">{i + 1}</span>
                  <input
                    type="text"
                    value={noms[i] || ''}
                    onChange={e => handleNom(i, e.target.value)}
                    placeholder={`Personne ${i + 1}`}
                  />
                  <div className="plage-cell">
                    <select
                      value={plages[i]?.heureDebut ?? HEURE_MIN}
                      onChange={e => handlePlage(i, 'heureDebut', Number(e.target.value))}
                      className="plage-select plage-h"
                    >
                      {HEURES_PLAGE.map(h => <option key={h} value={h}>{fmtH(h)}</option>)}
                    </select>
                    <select
                      value={plages[i]?.minuteDebut ?? 0}
                      onChange={e => handlePlage(i, 'minuteDebut', Number(e.target.value))}
                      className="plage-select plage-m"
                    >
                      {MINUTES_OPTIONS.map(m => <option key={m} value={m}>{fmtM(m)}</option>)}
                    </select>
                    <span className="plage-sep">→</span>
                    <select
                      value={plages[i]?.heureFin ?? HEURE_MAX}
                      onChange={e => handlePlage(i, 'heureFin', Number(e.target.value))}
                      className="plage-select plage-h"
                    >
                      {HEURES_PLAGE.map(h => <option key={h} value={h}>{fmtH(h)}</option>)}
                    </select>
                    <select
                      value={plages[i]?.minuteFin ?? MINUTE_MAX_FIN}
                      onChange={e => handlePlage(i, 'minuteFin', Number(e.target.value))}
                      className="plage-select plage-m"
                    >
                      {minutesFinOptions(i).map(m => <option key={m} value={m}>{fmtM(m)}</option>)}
                    </select>
                  </div>
                  <input
                    type="number"
                    value={p}
                    onChange={e => handlePoids(i, e.target.value)}
                    min="1"
                    className="input-poids"
                  />
                  <span className="row-pct">
                    {totalPoids > 0 ? ((p / totalPoids) * 100).toFixed(1) + ' %' : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>

          <div className="total-poids">Somme des poids : <strong>{totalPoids}</strong></div>

          {erreurCalcul && (
            <div className="form-error-banner">
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
              </svg>
              <span>{erreurCalcul}</span>
            </div>
          )}

          <div className="form-actions">
            <button type="button" className="btn-secondary" onClick={retour}>← Retour</button>
            <button type="button" className="btn-primary" onClick={handleSubmit}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
              Calculer la répartition
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
